import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { regenerateAccessCode, saveRecipients } from '../lib/api';
import { friendlyError } from '../lib/errors';
import { addRole, MAX_ROLES } from './RolesEditor';
import {
  RECIPIENT_ROLES, RECIPIENT_ROLE_INFO, CODE_CHANNELS, CODE_CHANNEL_LABELS,
  checkSendReadiness, roleColor,
  type Recipient, type RecipientInput, type RecipientRole, type CodeChannel,
} from '@beckersign/shared';

interface Role { roleId: string; name: string }
interface Props {
  envelopeId: string;
  roles: Role[];
  fields: { roleId: string; required: boolean }[];
  documentCount: number;
  readOnly: boolean;
}

type Draft = RecipientInput & { _id: string };

const NEW_ROLE = '__new__';

const blank = (roleId: string, order: number): Draft => ({
  _id: crypto.randomUUID().slice(0, 8),
  roleId, name: '', email: '', recipientRole: 'signer',
  routingOrder: order, authMethod: 'access_code',   // C-26: on by default
  codeChannel: 'phone',
});

export default function RecipientsEditor({ envelopeId, roles, fields, documentCount, readOnly }: Props) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [saved, setSaved] = useState<Recipient[]>([]);
  const [codes, setCodes] = useState<{ email: string; name: string; code: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const loaded = useState({ done: false })[0];

  useEffect(() => {
    const q = query(collection(db, 'envelopes', envelopeId, 'recipients'), orderBy('routingOrder'));
    return onSnapshot(q, (snap) => {
      const rs = snap.docs.map((d) => d.data() as Recipient);
      setSaved(rs);
      if (!loaded.done) {
        loaded.done = true;
        setDrafts(rs.length > 0
          ? rs.map((r) => ({ ...r, _id: r.id } as Draft))
          : [blank(roles[0]?.roleId ?? 'role1', 1)]);
      }
    });
  }, [envelopeId, roles, loaded]);

  const patch = (id: string, p: Partial<Draft>) =>
    setDrafts((ds) => ds.map((d) => (d._id === id ? { ...d, ...p } : d)));

  async function save() {
    setBusy(true); setErr(null); setCodes([]);
    try {
      const payload = drafts
        .filter((d) => d.name.trim() && d.email.trim())
        .map(({ _id, ...r }) => {
          // Delete the key rather than setting it to undefined: callables
          // serialise undefined to null, and null is not "absent".
          const out: Record<string, unknown> = { ...r };
          if (r.authMethod !== 'access_code') delete out.codeChannel;
          for (const k of ['phone', 'company', 'title']) {
            if (!out[k]) delete out[k];
          }
          return out;
        });
      const res = await saveRecipients({ envelopeId, recipients: payload as RecipientInput[] });
      setCodes(res.data.issuedCodes);
    } catch (e) {
      setErr(friendlyError(e, 'Could not save recipients.'));
    } finally { setBusy(false); }
  }

  // Roles live on the envelope; a recipient who needs one that doesn't exist
  // yet gets it created here rather than sending the user to the fields tab.
  async function pickRole(id: string, roleId: string) {
    if (roleId !== NEW_ROLE) { patch(id, { roleId }); return; }
    setErr(null);
    try {
      const role = await addRole(envelopeId, roles);
      patch(id, { roleId: role.roleId });
    } catch (e) {
      setErr(friendlyError(e, 'Could not add a signing role.'));
    }
  }

  async function addRecipient() {
    const taken = new Set(drafts.map((d) => d.roleId));
    const free = roles.find((r) => !taken.has(r.roleId));
    let roleId = free?.roleId ?? roles[0]?.roleId ?? 'role1';
    if (!free && roles.length < MAX_ROLES) {
      setErr(null);
      try { roleId = (await addRole(envelopeId, roles)).roleId; }
      catch (e) { setErr(friendlyError(e, 'Could not add a signing role.')); }
    }
    setDrafts((ds) => [...ds, blank(roleId, ds.length + 1)]);
  }

  async function newCode(r: Recipient) {
    if (!window.confirm(`Issue a new access code for ${r.name}? Their current code stops working immediately.`)) return;
    setErr(null);
    try {
      const res = await regenerateAccessCode({ envelopeId, recipientId: r.id });
      setCodes([{ email: r.email, name: r.name, code: res.data.code }]);
    } catch (e) {
      setErr(friendlyError(e, 'Could not issue a new code.'));
    }
  }

  const readiness = checkSendReadiness(saved, roles, fields, documentCount);
  const roleIdx = (id: string) => Math.max(0, roles.findIndex((r) => r.roleId === id));

  return (
    <div>
      <h2>Recipients</h2>
      <p className="muted">
        Attach people to the signing roles you defined. Recipients sharing a routing number are
        notified together; lower numbers go first.
      </p>

      {drafts.map((d) => {
        // Only a saved recipient has a code to replace, and only if it was saved as access_code.
        const savedRec = saved.find((s) => s.id === d._id);
        const canRenewCode = savedRec?.authMethod === 'access_code'
          && savedRec.status !== 'completed' && savedRec.status !== 'declined';
        return (
        <div key={d._id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '.85rem', marginBottom: '.6rem' }}>
          <div className="row" style={{ gap: '.5rem' }}>
            <input
              placeholder="Full name" value={d.name} disabled={readOnly} maxLength={120}
              onChange={(e) => patch(d._id, { name: e.target.value })}
              style={{ flex: 1, minWidth: '9rem', padding: '.45rem', minHeight: 40 }}
            />
            <input
              placeholder="email@example.com" type="email" value={d.email} disabled={readOnly} maxLength={254}
              onChange={(e) => patch(d._id, { email: e.target.value })}
              style={{ flex: 1.4, minWidth: '11rem', padding: '.45rem', minHeight: 40 }}
            />
            <button
              onClick={() => setDrafts((ds) => ds.filter((x) => x._id !== d._id))}
              disabled={readOnly || drafts.length <= 1}
            >Remove</button>
          </div>

          <div className="row" style={{ gap: '.5rem', marginTop: '.5rem' }}>
            <label style={{ fontSize: '.78rem' }}>
              Role
              <select
                value={d.recipientRole} disabled={readOnly}
                onChange={(e) => patch(d._id, { recipientRole: e.target.value as RecipientRole })}
                style={{ display: 'block', minHeight: 38 }}
              >
                {RECIPIENT_ROLES.map((r) => (
                  <option key={r} value={r}>{RECIPIENT_ROLE_INFO[r].label}</option>
                ))}
              </select>
            </label>

            <label style={{ fontSize: '.78rem' }}>
              Signs as
              <select
                value={d.roleId} disabled={readOnly}
                onChange={(e) => void pickRole(d._id, e.target.value)}
                style={{ display: 'block', minHeight: 38 }}
              >
                {roles.map((r) => <option key={r.roleId} value={r.roleId}>{r.name}</option>)}
                {roles.length < MAX_ROLES && <option value={NEW_ROLE}>+ New signing role</option>}
              </select>
            </label>

            <label style={{ fontSize: '.78rem' }}>
              Order
              <input
                type="number" min={1} max={50} value={d.routingOrder} disabled={readOnly}
                onChange={(e) => patch(d._id, { routingOrder: Number(e.target.value) || 1 })}
                style={{ display: 'block', width: '4rem', padding: '.35rem', minHeight: 38 }}
              />
            </label>

            <label style={{ fontSize: '.78rem' }}>
              Verify by
              <select
                value={d.authMethod} disabled={readOnly}
                onChange={(e) => patch(d._id, { authMethod: e.target.value as 'email_only' | 'access_code' })}
                style={{ display: 'block', minHeight: 38 }}
              >
                <option value="access_code">Access code (recommended)</option>
                <option value="email_only">Email link only</option>
              </select>
            </label>

            {d.authMethod === 'access_code' && (
              <label style={{ fontSize: '.78rem' }}>
                Give the code by
                <select
                  value={d.codeChannel ?? 'phone'} disabled={readOnly}
                  onChange={(e) => patch(d._id, { codeChannel: e.target.value as CodeChannel })}
                  style={{ display: 'block', minHeight: 38 }}
                >
                  {CODE_CHANNELS.map((c) => (
                    <option key={c} value={c}>{CODE_CHANNEL_LABELS[c]}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <p className="muted" style={{ fontSize: '.75rem', margin: '.5rem 0 0' }}>
            <span style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: 2,
              background: roleColor(roleIdx(d.roleId)), marginRight: 6,
            }} />
            {RECIPIENT_ROLE_INFO[d.recipientRole].hint}
          </p>

          {canRenewCode && (
            <button onClick={() => void newCode(savedRec)} style={{ marginTop: '.5rem' }}>
              New code
            </button>
          )}

          {d.authMethod === 'email_only' && (
            <p className="muted" style={{ fontSize: '.75rem', margin: '.35rem 0 0' }}>
              An emailed link proves control of a mailbox, nothing more. Two California appellate
              cases refused to enforce e-signatures on exactly that evidence.
            </p>
          )}
        </div>
        );
      })}

      <div className="row" style={{ gap: '.5rem', justifyContent: 'flex-start' }}>
        <button
          onClick={() => void addRecipient()}
          disabled={readOnly || drafts.length >= 20}
        >Add recipient</button>
        <button className="primary" style={{ width: 'auto' }} onClick={() => void save()} disabled={readOnly || busy}>
          {busy ? 'Saving…' : 'Save recipients'}
        </button>
      </div>

      {err && <div className="alert" role="alert" style={{ marginTop: '.75rem' }}>{err}</div>}

      {codes.length > 0 && (
        <div className="notice" role="status" style={{ marginTop: '.75rem' }}>
          <strong>Access codes — shown once. Write them down now.</strong>
          <p style={{ margin: '.4rem 0', fontSize: '.85rem' }}>
            Deliver each code by phone, text or in person. <strong>Never email it</strong> — a code
            in the same inbox as the link proves nothing.
          </p>
          <table>
            <tbody>
              {codes.map((c) => (
                <tr key={c.email}>
                  <td>{c.name}</td>
                  <td><code style={{ fontSize: '1.1rem', letterSpacing: '.12em' }}>{c.code}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ fontSize: '.9rem', marginTop: '1.5rem' }}>Ready to send?</h3>
      {readiness.ready ? (
        <div className="notice" style={{ borderColor: 'var(--ok)' }}>
          ✓ This envelope is complete. Review and send it below.
        </div>
      ) : (
        <ul className="muted" style={{ fontSize: '.85rem', paddingLeft: '1.1rem' }}>
          {readiness.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}
